import { api } from './client.js';

/** Must match `OBJECT_ROOT` on the server. */
export const OBJECT_ROOT = 'jnote/v1/';

export interface PutResult {
  ok: true;
  bytes: number;
}

export interface ListResult {
  keys: string[];
}

export interface StorageHealth {
  ok: boolean;
  provider: string | null;
  host: string;
}

/** Encodes each path segment so filenames with spaces or `+` survive. */
function encodeKey(key: string): string {
  return key
    .split('/')
    .map((segment) => encodeURIComponent(segment))
    .join('/');
}

/**
 * Direct URL for an object, for use in `<img src>` / `<a href>`.
 *
 * Same-origin GETs carry the session cookie, so no signing is needed here.
 */
export function objectURL(key: string): string {
  return `/api/objects/${encodeKey(key)}`;
}

export const storageApi = {
  get: (key: string) => api.get<Uint8Array>(`/objects/${encodeKey(key)}`, { responseType: 'binary' }),

  getText: (key: string) => api.get<string>(`/objects/${encodeKey(key)}`, { responseType: 'text' }),

  put: (key: string, data: Uint8Array | string, contentType = 'application/octet-stream') =>
    api.put<PutResult>(`/objects/${encodeKey(key)}`, data, contentType),

  putJson: (key: string, value: unknown) =>
    api.put<PutResult>(
      `/objects/${encodeKey(key)}`,
      typeof value === 'string' ? value : JSON.stringify(value),
      'application/json; charset=utf-8',
    ),

  remove: (key: string) => api.delete<{ ok: true }>(`/objects/${encodeKey(key)}`),

  list: (prefix = OBJECT_ROOT) =>
    api.get<ListResult>(`/list?prefix=${encodeURIComponent(prefix)}`),

  health: () => api.get<StorageHealth>('/storage/health'),
};
